import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Plus, 
  Search,
  MoreHorizontal,
  Check,
  X,
  Loader2,
  Shield,
  Edit2,
  UserX,
  Key
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminUsers = () => {
  const { isAdmin, authFetch } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [editingUser, setEditingUser] = useState(null);

  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'editor',
    password: ''
  });

  const fetchUsers = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/users`, {
        
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Error carregant usuaris');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        toast.success('Usuari creat correctament');
        setShowCreateModal(false);
        setNewUser({ email: '', name: '', role: 'editor', password: '' });
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error creant usuari');
      }
    } catch (error) {
      toast.error('Error creant usuari');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('Rol actualitzat');
        fetchUsers();
      } else {
        toast.error('Error actualitzant rol');
      }
    } catch (error) {
      toast.error('Error actualitzant rol');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Segur que vols desactivar aquest usuari?')) return;

    try {
      const response = await authFetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Usuari desactivat');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error desactivant usuari');
      }
    } catch (error) {
      toast.error('Error desactivant usuari');
    }
  };

  const handleSetPassword = async (userId, password) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/users/${userId}/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        toast.success('Contrasenya establerta');
        setShowPasswordModal(null);
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error establint contrasenya');
      }
    } catch (error) {
      toast.error('Error establint contrasenya');
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <Shield className="w-16 h-16 text-[#888888] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#333333]">Accés restringit</h2>
          <p className="text-[#666666] mt-2">Només els administradors poden gestionar usuaris.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-users">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E]">
              Gestió d'Usuaris
            </h1>
            <p className="text-[#666666] mt-1">
              Administra els usuaris del CMS
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#1A4D2E] hover:bg-[#153d25] text-white"
            data-testid="create-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nou usuari
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
          <Input
            placeholder="Cercar per nom o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="user-search"
          />
        </div>

        {/* Users table */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E] mx-auto" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-[#666666]">No s'han trobat usuaris</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#F9F9F7] border-b border-[#E5E5E5]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Usuari
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Tipus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Estat
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#666666] uppercase tracking-wider">
                    Accions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {filteredUsers.map((user) => (
                  <tr key={user.user_id} className="hover:bg-[#F9F9F7]" data-testid={`user-row-${user.user_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#1A4D2E] flex items-center justify-center text-white font-medium">
                            {user.name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-[#333333]">{user.name}</p>
                          <p className="text-sm text-[#888888]">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={user.role}
                        onChange={(e) => handleUpdateRole(user.user_id, e.target.value)}
                        className="text-sm border border-[#E5E5E5] rounded px-2 py-1 bg-white"
                      >
                        <option value="admin">Admin</option>
                        <option value="editor">Editor</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.auth_type === 'google' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.auth_type === 'google' ? 'Google' : 'Contrasenya'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.is_active !== false
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active !== false ? (
                          <><Check className="w-3 h-3" /> Actiu</>
                        ) : (
                          <><X className="w-3 h-3" /> Inactiu</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPasswordModal(user.user_id)}
                          title="Establir contrasenya"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        {user.is_active !== false && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivateUser(user.user_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Desactivar usuari"
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create user modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-[#333333] mb-4">Crear nou usuari</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Nom de l'usuari"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="email@exemple.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full border border-[#E5E5E5] rounded-md px-3 py-2"
                >
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Contrasenya (opcional)</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Deixar en blanc per Google auth"
                />
                <p className="text-xs text-[#888888]">
                  Si no s'especifica contrasenya, l'usuari haurà d'accedir amb Google.
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancel·lar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#1A4D2E] hover:bg-[#153d25] text-white"
                >
                  Crear usuari
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set password modal */}
      {showPasswordModal && (
        <PasswordModal
          userId={showPasswordModal}
          onClose={() => setShowPasswordModal(null)}
          onSubmit={handleSetPassword}
        />
      )}
    </AdminLayout>
  );
};

const PasswordModal = ({ userId, onClose, onSubmit }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('La contrasenya ha de tenir almenys 6 caràcters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les contrasenyes no coincideixen');
      return;
    }
    onSubmit(userId, password);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <h2 className="text-xl font-semibold text-[#333333] mb-4">Establir contrasenya</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label>Nova contrasenya</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Confirmar contrasenya</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="********"
              required
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel·lar
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#1A4D2E] hover:bg-[#153d25] text-white"
            >
              Establir
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUsers;
